import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { getFocusRingStyle, updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    variant: 'contained',
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      position: 'relative',
      boxShadow: 'none',
      textRendering: 'geometricPrecision',
      outline: 'none',
      minWidth: '44px',
      padding: '14px 24px',
      flexShrink: '0',
      // font
      ...theme.typography.button,
      // startIcon / endIcon
      '& .MuiButton-icon': {
        // make sure not to increase size of the Button due to padding
        marginTop: '-2px',
        marginBottom: '-2px',
        // icon itself
        '& .MuiSvgIcon-root': {
          fontSize: '20px',
        },
        // spinner inside
        '& .FlowSpinner-root': {
          marginTop: '-2px',
          marginBottom: '-2px',
        },
      },
      ...(ownerState.size === 'small' && {
        padding: '8px 14px',
        fontSize: '14px',
        lineHeight: '16px',
        // startIcon / endIcon
        '& .MuiButton-icon': {
          // make sure not to increase size of the Button due to padding
          marginTop: '-2px',
          marginBottom: '-2px',
          // icon itself
          '& .MuiSvgIcon-root': {
            fontSize: '17px',
          },
        },
      }),
      ...(ownerState.size === 'large' && {
        padding: '15px 25px',
        fontSize: '18px',
        lineHeight: '20px',
        // startIcon / endIcon
        '& .MuiButton-icon': {
          // make sure not to increase size of the Button due to padding
          marginTop: '-2px',
          marginBottom: '-2px',
          // icon itself
          '& .MuiSvgIcon-root': {
            fontSize: '22px',
          },
        },
      }),
      backgroundClip: 'padding-box',
      transition:
        'transform 0.3s ease-out, color 0.3s ease-out, background-color 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out, opacity 0.3s ease-out',
      // 50% border radius without skewing ratio
      borderRadius: '1000px',
      '&:hover': {
        boxShadow: 'none',
      },
      '&&:active': {
        boxShadow: 'none',
      },
      // focused style
      ...getFocusRingStyle(theme),
      // disabled style
      '&.Mui-disabled': {
        opacity: '1',
      },
    }),
    startIcon: ({ ownerState }) => ({
      flexShrink: '0',
      // space between text and icon
      marginRight: '10px',
      marginLeft: '0',
      ...(ownerState.size === 'small' && {
        marginRight: '5px',
      }),
      ...(ownerState.size === 'large' && {
        marginRight: '15px',
      }),
    }),
    endIcon: ({ ownerState }) => ({
      flexShrink: '0',
      // space between text and icon
      marginRight: '0',
      marginLeft: '10px',
      ...(ownerState.size === 'small' && {
        marginLeft: '5px',
      }),
      ...(ownerState.size === 'large' && {
        marginLeft: '15px',
      }),
    }),
    contained: ({ theme, ownerState }) => ({
      ...((ownerState.color === 'primary' || ownerState.color === 'success') && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.brand.secondary,
            color: theme.palette.onSurface.os900,
          },
          {
            backgroundColor: theme.palette.brandHover.secondary,
            color: theme.palette.onSurface.os900,
          },
        ),
      }),
      ...(ownerState.color === 'secondary' && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.onSurface.os600,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      ...(ownerState.color === 'white' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.transparent.tertiary,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      ...(ownerState.color === 'error' && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      // deprecated semantic colors, used by Production team
      ...(ownerState.color === 'warning' && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.warning.dark,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      ...(ownerState.color === 'info' && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.info.dark,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      ...(ownerState.color === 'inherit' && {
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: 'currentColor',
          },
          {
            backgroundColor: theme.palette.surface.primary,
          },
        ),
      }),
      // disabled style
      '&.Mui-disabled': {
        color: theme.palette.disabled.tertiary,
        backgroundColor: theme.palette.disabled.secondary,
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.onSurface.os800,
        }),
      },
    }),
    text: ({ theme, ownerState }) => ({
      ...getInteractiveStyle(
        {
          backgroundColor: 'transparent',
        },
        {
          backgroundColor: ['white', 'inherit'].includes(ownerState.color)
            ? theme.palette.transparent.tertiary
            : theme.palette.transparent.secondary,
        },
      ),
      ...((ownerState.color === 'primary' || ownerState.color === 'success') && {
        color: theme.palette.brand.secondary,
      }),
      ...(ownerState.color === 'secondary' && {
        color: theme.palette.text.primary,
      }),
      ...(ownerState.color === 'white' && {
        color: theme.palette.onSurface.os0,
      }),
      // disabled style
      '&.Mui-disabled': {
        color: theme.palette.disabled.primary,
      },
    }),
    outlined: ({ theme, ownerState }) => ({
      '&, &:hover, &.Mui-disabled': {
        borderWidth: '2px',
      },
      ...updateFocusRingStyle(2),
      borderStyle: 'solid',
      // remove 2px border from each side and move focusRing
      padding: '12px 22px',
      ...(ownerState.size === 'small' && {
        '&, &:hover, &.Mui-disabled': {
          borderWidth: '1px',
        },
        ...updateFocusRingStyle(1),
        padding: '7px 13px',
      }),
      ...(ownerState.size === 'large' && {
        padding: '13px 23px',
      }),
      // electric green variant
      ...((ownerState.color === 'primary' || ownerState.color === 'success') && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.brand.secondary,
            borderColor: theme.palette.brand.secondary,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: theme.palette.brand.secondary,
          },
        ),
      }),
      // secondary used as text color
      ...(ownerState.color === 'secondary' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            borderColor: theme.palette.text.primary,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: theme.palette.text.primary,
          },
        ),
      }),
      ...(ownerState.color === 'white' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.onSurface.os0,
            borderColor: theme.palette.onSurface.os0,
          },
          {
            backgroundColor: theme.palette.transparent.tertiary,
            color: theme.palette.onSurface.os0,
          },
        ),
      }),
      ...(ownerState.color === 'error' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: theme.palette.error.main,
          },
        ),
      }),
      // deprecated semantic colors
      ...(ownerState.color === 'warning' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.warning.main,
            borderColor: theme.palette.warning.main,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: theme.palette.warning.main,
          },
        ),
      }),
      ...(ownerState.color === 'info' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.info.main,
            borderColor: theme.palette.info.main,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
            color: theme.palette.info.main,
          },
        ),
      }),
      ...(ownerState.color === 'inherit' && {
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: 'currentColor',
            borderColor: 'currentColor',
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
          },
        ),
      }),
      // disabled style
      '&.Mui-disabled': {
        borderColor: theme.palette.disabled.primary,
        color: theme.palette.disabled.primary,
      },
    }),
  },
  variants: [
    {
      props: { color: 'surface' },
      style: ({ theme }) => ({
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.transparent.quarternary : theme.palette.onSurface.os100}`,
        ...updateFocusRingStyle(1),
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.surface.primary,
            color: theme.palette.text.primary,
          },
          {
            backgroundColor: theme.palette.surface.secondary,
            color: theme.palette.text.primary,
          },
        ),
        // disabled style
        '&.Mui-disabled': {
          backgroundColor: theme.palette.surface.secondary,
          borderColor: theme.palette.surface.secondary,
          color: theme.palette.disabled.primary,
        },
      }),
    },
    {
      props: { variant: 'contained', color: 'surface' },
      style: () => ({
        // remove 1px border from each side
        padding: '13px 23px',
      }),
    },
    {
      props: { variant: 'contained', color: 'surface', size: 'small' },
      style: () => ({
        // remove 1px border from each side
        padding: '7px 13px',
      }),
    },
    {
      props: { variant: 'contained', color: 'surface', size: 'large' },
      style: () => ({
        // remove 1px border from each side
        padding: '14px 24px',
      }),
    },
    {
      props: { variant: 'outlined', color: 'surface' },
      style: () => ({
        borderWidth: 2,
        ...updateFocusRingStyle(2),
      }),
    },
    {
      props: { variant: 'text', color: 'surface' },
      style: () => ({
        borderWidth: 0,
        backgroundColor: 'transparent',
        ...updateFocusRingStyle(),
      }),
    },
    {
      props: { variant: 'navigation' },
      style: ({ theme }) => ({
        padding: '0',
        fontSize: '14px',
        borderRadius: '2px',
        letterSpacing: '0.02em',
        fontWeight: '400',
        lineHeight: '20px',
        '& .MuiButton-icon': {
          transition: 'transform 0.3s ease-out',
          // icon itself
          '& .MuiSvgIcon-root': {
            fontSize: '17px',
          },
        },
        '& .MuiButton-startIcon': {
          marginRight: '6px',
        },
        '& .MuiButton-endIcon': {
          marginLeft: '6px',
        },
        // prepare pseudo-element for focus effect
        '&::before': {
          inset: '-4px -8px',
        },
        // same style is used in the quickLink interactive effect
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.mode === 'dark' ? theme.palette.onSurface.os0 : theme.palette.onSurface.os900,
            '& .MuiButton-startIcon': {
              transform: 'translateX(0px)',
            },
            '& .MuiButton-endIcon': {
              transform: 'translateX(0px)',
            },
          },
          {
            backgroundColor: 'transparent',
            color: theme.palette.mode === 'dark' ? theme.palette.onSurface.os300 : theme.palette.onSurface.os700,
            '& .MuiButton-startIcon': {
              transform: 'translateX(-8px)',
            },
            '& .MuiButton-endIcon': {
              transform: 'translateX(8px)',
            },
          },
        ),
        // disabled style
        '&.Mui-disabled': {
          color: theme.palette.disabled.tertiary,
        },
      }),
    },
    {
      props: { variant: 'navigation', color: 'primary' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            color: theme.palette.primary.main,
          },
          {
            color: theme.palette.brandHover.primary,
          },
        ),
      }),
    },
    {
      props: { variant: 'navigation', color: 'surface' },
      style: {
        border: 'none',
        '&&::before': {
          inset: '-4px -8px',
        },
      },
    },
    {
      props: { variant: 'navigation', color: 'white' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            color: theme.palette.onSurface.os0,
          },
          {
            color: theme.palette.onSurface.os300,
          },
        ),
      }),
    },
    {
      props: { variant: 'navigation', color: 'error' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            color: theme.palette.error.main,
          },
          {
            color: theme.palette.error.dark,
          },
        ),
      }),
    },
    {
      props: { variant: 'shortcut' },
      style: ({ theme }) => ({
        // adjust proportions
        borderRadius: '3px',
        padding: '2px 8px',
        // font
        textAlign: 'left',
        fontWeight: '300',
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.02em',
        '&&': {
          textDecoration: 'underline',
          textDecorationColor: 'currentColor',
          textDecorationThickness: '1px',
          textUnderlineOffset: '2px',
        },
        // startIcon / endIcon
        '&& .MuiButton-icon': {
          transition: 'transform 0.3s ease-out',
        },
        // style
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            '& .MuiButton-icon': {
              transform: 'scale(1.0)',
            },
          },
          {
            backgroundColor: 'transparent',
            color: theme.palette.mode === 'dark' ? theme.palette.onSurface.os300 : theme.palette.onSurface.os700,
            '& .MuiButton-icon': {
              transform: 'scale(1.1)',
            },
          },
        ),
        // responsive scale
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          // startIcon / endIcon
          '& .MuiButton-icon': {
            // icon itself
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
            },
          },
        },
        // disabled style
        '&.Mui-disabled': {
          color: theme.palette.disabled.tertiary,
        },
      }),
    },
    {
      props: { variant: 'shortcut', fullWidth: true },
      style: {
        // move content to left side from the center
        justifyContent: 'flex-start',
        textAlign: 'left',
      },
    },
    {
      props: { variant: 'shortcut', color: 'inherit' },
      style: {
        ...getInteractiveStyle(
          {
            color: 'currentColor',
          },
          {
            color: 'currentColor',
          },
        ),
      },
    },
  ],
};
